import React, { useCallback, useMemo } from 'react';
import InvoicingProduct from 'shared/db/services/InvoicingProduct';
import { ParsedInvoiceProduct } from 'shared/types/Invoice';

import {
    AddEditInvoicingProductModal,
    FormValues,
} from '@/components/AddEditInvoicingProductModal';
import { useImagesContext } from '@/context/ImagesContext';
import { useDatabase } from '@/hooks';
import { databaseValidation } from '@/services/databaseValidation';
import { dbServiceLogger } from '@/services/logger/logger';

type Props = {
    close: VoidFunction;
    isOpen: boolean;
    onProductCreated: (product: ParsedInvoiceProduct) => void;
    userId: string;
    organisationId: string;
};

function CreateInvoicingProductModal({
    close,
    isOpen,
    onProductCreated,
    userId,
    organisationId,
}: Props) {
    const { getDatabase } = useDatabase();
    const { ImagesService } = useImagesContext();

    const invoicingProductService = useMemo(
        () =>
            new InvoicingProduct({
                database: getDatabase(),
                logger: dbServiceLogger,
                imageService: ImagesService,
                validation: databaseValidation,
            }),
        [getDatabase, ImagesService],
    );

    const handleSave = useCallback(
        async (values: FormValues) => {
            const product = await invoicingProductService.addProduct({
                name: values.name.trim(),
                price: values.price,
                available: values.available,
                organisationID: organisationId,
                userID: userId,
            });

            onProductCreated({
                invoicingProductId: product.id,
                name: product.name,
                price: product.price,
                userId: product.userId,
                organisationId: product.organisationId,
                quantity: 1,
            });

            close();
        },
        [
            invoicingProductService,
            organisationId,
            userId,
            onProductCreated,
            close,
        ],
    );

    return (
        <AddEditInvoicingProductModal
            isOpen={isOpen}
            onClose={close}
            onSave={handleSave}
        />
    );
}

export default CreateInvoicingProductModal;
